import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import PageHero from '../../components/ui/page-hero';
import { 
  MapPin, 
  Clock, 
  Users, 
  Heart, 
  Coffee, 
  GraduationCap, 
  Trophy,
  Star,
  ArrowRight,
  Briefcase,
  DollarSign,
  Calendar,
  Globe,
  Mail,
  Phone,
  Smile
} from 'lucide-react';

const Careers = () => {
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    coverLetter: '',
    resume: null
  });

  // Mock Job Listings
  const jobListings = [
    {
      id: 1,
      title: 'Senior Full-Stack Developer',
      department: 'Engineering',
      location: 'Casablanca, Morocco',
      type: 'Full-time',
      experience: '3-5 years',
      salary: '15,000 - 25,000 MAD',
      posted: '2 days ago',
      description: 'We are looking for a passionate Senior Full-Stack Developer to join our engineering team. You will be responsible for developing and maintaining web applications using modern technologies.',
      requirements: [
        '3+ years of experience with React, Node.js, and PostgreSQL',
        'Strong understanding of TypeScript and modern JavaScript',
        'Experience with cloud platforms (AWS, Azure, or GCP)',
        'Knowledge of DevOps practices and CI/CD pipelines',
        'Excellent problem-solving and communication skills',
        'Experience with agile development methodologies'
      ],
      benefits: [
        'Competitive salary and performance bonuses',
        'Flexible working hours and remote work options',
        'Professional development budget',
        'Health insurance and wellness programs',
        'Modern office in Casablanca with great amenities'
      ],
      skills: ['React', 'Node.js', 'TypeScript', 'PostgreSQL', 'AWS', 'Docker'],
      urgent: true
    },
    {
      id: 2,
      title: 'AI/ML Engineer',
      department: 'AI & Innovation',
      location: 'Remote / Casablanca',
      type: 'Full-time',
      experience: '2-4 years',
      salary: '18,000 - 30,000 MAD',
      posted: '1 week ago',
      description: 'Join our AI team to build cutting-edge machine learning solutions and AI-powered applications. Work on projects that shape the future of technology in Morocco.',
      requirements: [
        '2+ years of experience in machine learning and AI',
        'Proficiency in Python, TensorFlow, and PyTorch',
        'Experience with LLMs and AI model integration',
        'Knowledge of data science and analytics',
        'Strong mathematical and statistical background',
        'Experience with cloud ML platforms'
      ],
      benefits: [
        'Cutting-edge AI projects and research opportunities',
        'Conference attendance and training budget',
        'Access to latest AI tools and technologies',
        'Collaborative work environment',
        'Career growth in emerging AI field'
      ],
      skills: ['Python', 'TensorFlow', 'PyTorch', 'OpenAI', 'LangChain', 'AWS'],
      urgent: false
    },
    {
      id: 3,
      title: 'DevOps Engineer',
      department: 'Infrastructure',
      location: 'Casablanca, Morocco',
      type: 'Full-time',
      experience: '2-4 years',
      salary: '16,000 - 24,000 MAD',
      posted: '3 days ago',
      description: 'We need a skilled DevOps Engineer to help us scale our infrastructure and improve our deployment processes. You will work with cutting-edge cloud technologies.',
      requirements: [
        '2+ years of DevOps or infrastructure experience',
        'Strong knowledge of Docker, Kubernetes, and CI/CD',
        'Experience with cloud platforms (AWS preferred)',
        'Knowledge of infrastructure as code (Terraform)',
        'Experience with monitoring and logging tools',
        'Strong scripting skills (Bash, Python)'
      ],
      benefits: [
        'Work with modern cloud infrastructure',
        'Continuous learning and certification support',
        'Flexible schedule and remote work options',
        'Competitive compensation package',
        'Great team culture and collaboration'
      ],
      skills: ['Docker', 'Kubernetes', 'AWS', 'Terraform', 'CI/CD', 'Monitoring'],
      urgent: false
    },
    {
      id: 4,
      title: 'UI/UX Designer',
      department: 'Design',
      location: 'Casablanca, Morocco',
      type: 'Full-time',
      experience: '2-3 years',
      salary: '12,000 - 18,000 MAD',
      posted: '5 days ago',
      description: 'Create beautiful and intuitive user experiences for our digital products. Work closely with our development team to bring designs to life.',
      requirements: [
        '2+ years of UI/UX design experience',
        'Proficiency in Figma, Adobe Creative Suite',
        'Strong portfolio showcasing design skills',
        'Understanding of user research and testing',
        'Knowledge of design systems and accessibility',
        'Experience with web and mobile design'
      ],
      benefits: [
        'Creative freedom and design ownership',
        'Latest design tools and software',
        'Collaboration with talented developers',
        'Portfolio-building opportunities',
        'Design community involvement'
      ],
      skills: ['Figma', 'Adobe XD', 'Sketch', 'Prototyping', 'User Research', 'Design Systems'],
      urgent: false
    },
    {
      id: 5,
      title: 'Business Development Manager',
      department: 'Sales & Growth',
      location: 'Casablanca, Morocco',
      type: 'Full-time',
      experience: '3-5 years',
      salary: '20,000 - 35,000 MAD + Commission',
      posted: '1 week ago',
      description: 'Drive business growth by identifying new opportunities, building client relationships, and expanding our market presence in Morocco and Africa.',
      requirements: [
        '3+ years of business development experience',
        'Strong network in Moroccan business community',
        'Experience in IT services or technology sales',
        'Excellent communication and negotiation skills',
        'Results-driven with proven track record',
        'Fluent in Arabic, French, and English'
      ],
      benefits: [
        'Unlimited earning potential with commissions',
        'Expense account for client entertainment',
        'Company car and mobile phone',
        'Leadership development opportunities',
        'Direct impact on company growth'
      ],
      skills: ['Sales', 'Negotiation', 'CRM', 'Market Analysis', 'Client Relations', 'Strategy'],
      urgent: true
    },
    {
      id: 6,
      title: 'Junior Frontend Developer',
      department: 'Engineering',
      location: 'Casablanca, Morocco',
      type: 'Full-time',
      experience: '0-2 years',
      salary: '8,000 - 12,000 MAD',
      posted: '1 week ago',
      description: 'Perfect opportunity for a junior developer to start their career in a supportive environment. Work on real projects with mentorship from senior developers.',
      requirements: [
        'Basic knowledge of HTML, CSS, and JavaScript',
        'Familiarity with React or willingness to learn',
        'Understanding of responsive design principles',
        'Strong desire to learn and grow',
        'Good problem-solving skills',
        'Team player with positive attitude'
      ],
      benefits: [
        'Comprehensive mentorship program',
        'Learning budget for courses and books',
        'Gradual increase in responsibilities',
        'Career progression opportunities',
        'Supportive team environment'
      ],
      skills: ['HTML', 'CSS', 'JavaScript', 'React', 'Git', 'Responsive Design'],
      urgent: false
    }
  ];

  // Company Culture & Benefits
  const companyCulture = [
    {
      icon: Heart,
      title: 'Work-Life Balance',
      description: 'Flexible hours, remote work options, and generous vacation time to ensure our team stays happy and productive.'
    },
    {
      icon: GraduationCap,
      title: 'Learning & Growth',
      description: 'Annual learning budget, conference attendance, and mentorship programs to help you advance your career.'
    },
    {
      icon: Users,
      title: 'Collaborative Environment',
      description: 'Open communication, team building activities, and a supportive culture where everyone\'s voice matters.'
    },
    {
      icon: Trophy,
      title: 'Recognition & Rewards',
      description: 'Performance bonuses, employee recognition programs, and opportunities for career advancement.'
    },
    {
      icon: Coffee,
      title: 'Great Perks',
      description: 'Free coffee, team lunches, modern office space, and health insurance to keep you comfortable and healthy.'
    },
    {
      icon: Globe,
      title: 'Global Impact',
      description: 'Work on projects that make a difference in Morocco and contribute to the country\'s digital transformation.'
    }
  ];

  // Employee Testimonials
  const testimonials = [
    {
      name: 'Ahmed El Mansouri',
      role: 'Senior Full-Stack Developer',
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Ahmed', // Removed external API dependency
      quote: 'Working at Envael has been incredible. The team is supportive, the projects are challenging, and I\'ve grown so much as a developer.',
      rating: 5
    },
    {
      name: 'Fatima Alami',
      role: 'AI/ML Engineer',
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Fatima', // Removed external API dependency
      quote: 'The learning opportunities here are amazing. I get to work with cutting-edge AI technologies and contribute to Morocco\'s digital future.',
      rating: 5
    },
    {
      name: 'Youssef Benali',
      role: 'DevOps Engineer',
      // image: 'https://api.dicebear.com/7.x/initials/svg?seed=Youssef', // Removed external API dependency
      quote: 'The culture here is fantastic. Great work-life balance, supportive management, and exciting technical challenges every day.',
      rating: 5
    }
  ];

  // Career Statistics
  const careerStats = [
    { number: '50+', label: 'Team Members', icon: Users },
    { number: '15+', label: 'Countries Represented', icon: Globe },
    { number: '95%', label: 'Employee Satisfaction', icon: Smile },
    { number: '3+', label: 'Years Average Tenure', icon: Calendar }
  ];

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setApplicationForm(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Application submitted:', applicationForm);
    alert('Application submitted successfully! We\'ll get back to you soon.');
  };

  return (
    <>
      <Helmet>
        <title>Careers & Jobs | Join Envael Team | Digital Transformation Morocco</title>
        <meta name="description" content="Join Envael's growing team in Morocco. Explore career opportunities in digital transformation, web development, AI solutions, and business growth. Competitive benefits, remote work options, and career development programs." />
        <meta name="keywords" content="careers morocco, IT jobs casablanca, web developer jobs, AI engineer positions, digital marketing careers, remote work morocco, tech jobs kenitra, envael careers, software development jobs" />
        <meta property="og:title" content="Careers & Jobs | Join Envael Team | Digital Transformation Morocco" />
        <meta property="og:description" content="Join Envael's growing team in Morocco. Explore career opportunities in digital transformation, web development, AI solutions, and business growth." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://envael.com/careers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Careers & Jobs | Join Envael Team | Digital Transformation Morocco" />
        <meta name="twitter:description" content="Join Envael's growing team in Morocco. Explore career opportunities in digital transformation, web development, AI solutions, and business growth." />
        <link rel="canonical" href="https://envael.com/careers" />
      </Helmet>

      <div className="min-h-screen bg-background">
      {/* Page Hero */}
      <PageHero 
        pageName="Careers" 
        subtitle="Join our mission to transform Morocco's digital landscape. Build the future with cutting-edge technology and innovative solutions."
        size="large"
        fullHeight={true}
        enableSpline={true}
      />

      {/* Career Stats */}
      <section className="py-16 bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {careerStats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <stat.icon className="h-8 w-8 text-primary" />
                </div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">{stat.number}</div>
                <div className="text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Culture */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Why Work With Us?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We believe in creating an environment where talented people can thrive, grow, and make a real impact
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companyCulture.map((item, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30">
                <CardContent className="p-8 text-center">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <item.icon className="h-8 w-8 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Open Positions
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Explore exciting career opportunities and find your perfect role in our growing team
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {jobListings.map((job) => (
              <Card key={job.id} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-foreground">{job.title}</h3>
                        {job.urgent && (
                          <Badge variant="destructive" className="text-xs">Urgent</Badge>
                        )}
                      </div>
                      <p className="text-primary font-medium">{job.department}</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      asChild
                    >
                      <a href="#application-form">View Details</a>
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      {job.location}
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      {job.type}
                    </div>
                    <div className="flex items-center gap-2">
                      <Briefcase className="h-4 w-4" />
                      {job.experience}
                    </div>
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      {job.salary}
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4 leading-relaxed">{job.description}</p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {job.skills.map((skill) => (
                      <Badge key={skill} variant="secondary" className="text-xs">
                        {skill}
                      </Badge>
                    ))}
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Posted {job.posted}</span>
                    <Button 
                      size="sm"
                      className="group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                      asChild
                    >
                      <a href="#application-form">
                        Apply Now
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Testimonials */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              What Our Team Says
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Hear from our team members about their experience working at Envael
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30">
                <CardContent className="p-8">
                  <div className="flex items-center mb-6">
                    <Avatar className="h-12 w-12 mr-4">
                      <AvatarImage src={testimonial.image} alt={testimonial.name} />
                      <AvatarFallback>{testimonial.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div>
                      <h4 className="font-bold text-foreground">{testimonial.name}</h4>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  <p className="text-muted-foreground leading-relaxed italic">
                    "{testimonial.quote}"
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-muted/30" id="application-form">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
              Ready to Join Us?
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Don't see a position that fits? Send us your application and we'll keep you in mind for future opportunities
            </p>
          </div>

          <Card className="border-border/50">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={applicationForm.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={applicationForm.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={applicationForm.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="+212 6 63 08 13 83"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Position of Interest
                    </label>
                    <select
                      name="position"
                      value={applicationForm.position}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">Select a position</option>
                      {jobListings.map((job) => (
                        <option key={job.id} value={job.title}>{job.title}</option>
                      ))}
                      <option value="other">Other (specify in cover letter)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Years of Experience
                  </label>
                  <select
                    name="experience"
                    value={applicationForm.experience}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select experience level</option>
                    <option value="0-1">0-1 years (Entry Level)</option>
                    <option value="1-3">1-3 years (Junior)</option>
                    <option value="3-5">3-5 years (Mid-level)</option>
                    <option value="5+">5+ years (Senior)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Cover Letter *
                  </label>
                  <textarea
                    name="coverLetter"
                    value={applicationForm.coverLetter}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Tell us about yourself, your experience, and why you want to join Envael..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Resume/CV *
                  </label>
                  <input
                    type="file"
                    name="resume"
                    onChange={handleInputChange}
                    accept=".pdf,.doc,.docx"
                    required
                    className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>

                <div className="flex justify-center">
                  <Button type="submit" size="lg" className="px-12 py-6 text-lg">
                    Submit Application
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              Questions About Careers?
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              Get in touch with our HR team for more information
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" size="lg" className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                contact@envael.com
              </Button>
              <Button variant="outline" size="lg" className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                +212 6 63 08 13 83
              </Button>
        </div>
      </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Careers;
